import type { Movimiento } from "./tipos"

/**
 * Cuentas de dinero en la pantalla, en CÉNTIMOS ENTEROS.
 *
 * ⚠ **Nunca con `Number` y decimales.** En coma flotante 0,1 + 0,2 da
 * 0,30000000000000004, y un saldo corrido encadena cientos de líneas: el error se
 * acumula y el último saldo del extracto deja de coincidir con el de la cuenta por
 * céntimos que nadie sabe explicar. El servidor manda los montos como texto decimal
 * —son `Decimal`, regla 18— y aquí se pasan a `bigint` de céntimos, que suma exacto.
 */

const DECIMAL = /^([+-])?(\d+)(?:\.(\d+))?$/

/** «1500.00», «-3.5» o «12» a céntimos exactos. */
export function aCentimos(texto: string): bigint {
  const partes = DECIMAL.exec(texto.trim())
  if (partes === null) throw new Error(`«${texto}» no es un monto`)
  const [, signo, entero = "0", fraccion = ""] = partes
  // Los montos son `Numeric(18, 2)`: nunca traen más de dos decimales.
  const centimos = BigInt(entero) * 100n + BigInt(`${fraccion}00`.slice(0, 2))
  return signo === "-" ? -centimos : centimos
}

/** Céntimos a «1.234.567,89», como se escriben aquí las cifras. */
export function comoMonto(centimos: bigint): string {
  const negativo = centimos < 0n
  const absoluto = negativo ? -centimos : centimos
  const entero = (absoluto / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  const resto = (absoluto % 100n).toString().padStart(2, "0")
  return `${negativo ? "-" : ""}${entero},${resto}`
}

/** Un monto tal como llega del servidor, directo a «1.234,56». */
export function monto(texto: string): string {
  return comoMonto(aCentimos(texto))
}

/**
 * Una CANTIDAD como se lee: «1», «1,5», «2,25». Nunca «1.000000».
 *
 * ⚠ **La columna guarda seis decimales y eso no cambia.** `cantidad` comparte tipo
 * con las tasas —dos precisiones en todo el sistema financiero, y sólo dos—, así que
 * el servidor devuelve `1.000000`. Lo que sobra son los ceros de relleno, y eso es
 * cosa de cómo se escribe, no de cómo se guarda: recortar la columna para que se lea
 * bien obligaría a una migración y dejaría fuera la tasa, que sí los necesita.
 *
 * ⚠ **Se corta en dos decimales, no se redondea a escondidas**: se compran dos
 * cajas y media, no 2,3333 cajas. Si alguna vez hiciera falta más precisión, lo que
 * cambia es esta función y no el esquema.
 */
/**
 * El mismo número, pero para meterlo en un `<input>`: «1», «1.5», con PUNTO.
 *
 * ⚠ **No se localiza.** Lo que sale de aquí vuelve al servidor tal cual, y un
 * «1,5» con coma no es un decimal para nadie salvo para quien lo lee en pantalla.
 */
export function paraEditar(texto: string): string {
  const numero = Number(texto.trim())
  return Number.isFinite(numero) ? String(numero) : texto.trim()
}

export function comoCantidad(texto: string): string {
  const limpio = texto.trim()
  if (limpio === "") return ""
  const numero = Number(limpio)
  if (!Number.isFinite(numero)) return limpio
  // `es-VE`: coma decimal y punto de millar, como el resto de las cifras.
  return numero.toLocaleString("es-VE", { maximumFractionDigits: 2 })
}

export type LineaExtracto = Movimiento & {
  /** El saldo de la cuenta justo después de este movimiento; `null` si está anulado. */
  saldoDespues: bigint | null
}

/**
 * El saldo después de cada movimiento, calculado HACIA ATRÁS desde el de hoy.
 *
 * ⚠ **Hacia atrás y no desde cero**, porque el extracto va con tope: con los 200 más
 * recientes no se puede sumar desde el primero, pero sí restar desde el saldo que da
 * el servidor, que sale de TODO el ledger. Los movimientos llegan del más nuevo al
 * más viejo (`bancos.listar_transacciones`).
 *
 * ⚠ **Una anulada no mueve el saldo**: no cuenta en el del servidor, así que tampoco
 * aquí. Se queda en su sitio, tachada y sin saldo, que es lo que la distingue.
 */
export function conSaldoCorrido(
  movimientos: readonly Movimiento[],
  saldoActual: string,
): LineaExtracto[] {
  let saldo = aCentimos(saldoActual)
  return movimientos.map((m) => {
    if (m.anulado) return { ...m, saldoDespues: null }
    const linea = { ...m, saldoDespues: saldo }
    const importe = aCentimos(m.monto)
    saldo = m.tipo === "INGRESO" ? saldo - importe : saldo + importe
    return linea
  })
}
